import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { OrgChart, State } from 'd3-org-chart';
import { ActionButtonContainer } from './components/atoms/containers/action-button-container';
import { ActionButton } from './components/atoms/buttons/action-button';
import jspdf from 'jspdf';
import { ArrowButtonContainer } from './components/atoms/containers/arrow-button-contianer';
import { LogoContainer } from './components/atoms/containers/logo-container';
import logo from './assets/logos/logo.png'
import * as d3 from 'd3';

interface OrgChartComponentProps {
    data?: any[]
}

export const OrgChartComponent = ({ data }: OrgChartComponentProps) => {
    const d3Container = useRef(null);
    const chartRef = useRef<any>(null);
    const [index, setIndex] = useState(0);
    const [compact, setCompact] = useState(0);
    const [actNdCent, setActNdCent] = useState(0);
    const [showControls, setShowControls] = useState<boolean>(true);

    const getViewPortDemensions = useCallback(() => ({
        width: window.innerWidth,
        height: window.innerHeight
    }), [])

    const updateChartDemensions = useCallback(() => {
        if (chartRef.current) {
            const { width, height } = getViewPortDemensions();
            chartRef.current.svgWidth(width).svgHeight(height).render()
        }
    }, [getViewPortDemensions])

    useLayoutEffect(() => {
        if (data && d3Container.current) {

            const { width, height } = getViewPortDemensions();
            chartRef.current = new OrgChart()
                .container(d3Container.current)
                .data(data)
                .svgWidth(width)
                .svgHeight(height)
                .nodeWidth((d) => 280)
                .nodeHeight((d) => 80)
                .siblingsMargin(() => 50)
                .childrenMargin(() => 100)
                .neighbourMargin((node1, node2) => 40)
                .svgHeight(1000)
                .layout('left')
                .onNodeClick((d: any) => {
                    const mockEvent = {
                        stopPropagation: () => { },
                        preventDefault: () => { }
                    };

                    if (d.children || d._children) {
                        chartRef.current.onButtonClick(mockEvent, d);
                    }
                })
                .buttonContent(() => '')
                .compact(false)
                .compactMarginBetween((d) => 50)
                .nodeButtonWidth(() => 0)
                .nodeButtonHeight(() => 0)
                .rootMargin(40)
                .linkUpdate((d: any, i: number, arr: any[]) => {
                    d3.select(arr[i] as any)  // Cast to any to avoid TypeScript issues
                        .attr("stroke", "black")
                        .attr("stroke-width", 2);
                })
                .nodeContent((d: any, i: number, arr: any[], state: any) => {
                    const node = d.data;
                    return `
                        <div style="
                            width: ${d.width}px; 
                            height: ${d.height}px; 
                            background: #6077BA;
                            border-radius: 12px;
                            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                            padding: 16px;
                            color: white;
                            font-family: 'Arial', sans-serif;
                            position: relative;
                            overflow: hidden;
                            cursor: pointer;
                            transition: all 0.3s ease;
                        ">
                            <div style="
                                font-size: 24px;
                                font-weight: bold;
                                text-align: center;
                                margin-bottom: 4px;
                                text-shadow: 0 1px 2px rgba(0,0,0,0.1);
                            ">
                                ${node.name || 'Unknown'}
                            </div>
                            <div style="width: 50px; height: 1px; background-color: #F26722; margin: 8px auto;"></div>
                            <div style="
                                font-size: 18px;
                                text-align: center;
                                opacity: 0.9;
                                margin-bottom: 8px;
                            ">
                                ${node.positionName || 'No title'}
                            </div>
                        </div>
                    `;
                })
                .render();
        }

        chartRef.current && chartRef.current.expandAll().fit();

        const handleResize = () => {
            updateChartDemensions();
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [data]);

    // Action handlers
    const handleExpand = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setExpanded(data[0].id).render();
        }
    };

    const handleCollapse = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setExpanded(data[0].id, false).render();
        }
    };

    const handleFit = () => {
        if (chartRef.current) {
            chartRef.current.fit();
        }
    };

    const handleSwap = () => {
        if (chartRef.current) {
            const layouts = ["right", "bottom", "left", "top"];
            const newIndex = (index + 1) % 4;
            setIndex(newIndex);
            chartRef.current.layout(layouts[newIndex]).render().fit();
        }
    };

    const handleCompact = () => {
        if (chartRef.current) {
            const newCompact = (compact + 1) % 2;
            setCompact(newCompact);
            chartRef.current.compact(!!newCompact).render().fit();
        }
    };

    const handleCenterActive = () => {
        if (chartRef.current) {
            const newActNdCent = (actNdCent + 1) % 2;
            setActNdCent(newActNdCent);
            chartRef.current.setActiveNodeCentered(!!newActNdCent).render();
        }
    };

    const handleCenter = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setCentered(data[0].id).render();
        }
    };

    const handleMark = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setHighlighted(data[0].id).render();
        }
    };

    const handleMarkRoot = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setUpToTheRootHighlighted(data[0].id).render().fit();
        }
    };

    const handleClearMark = () => {
        if (chartRef.current) {
            chartRef.current.clearHighlighting();
        }
    };

    const handleFullscreen = () => {
        if (chartRef.current) {
            chartRef.current.fullscreen('body');
        }
    };

    const handleExportImg = () => {
        if (chartRef.current) {
            chartRef.current.exportImg();
        }
    };

    const handleExportFullImg = () => {
        if (chartRef.current) {
            chartRef.current.exportImg({ full: true });
        }
    };

    const handleExportSvg = () => {
        if (chartRef.current) {
            chartRef.current.exportSvg();
        }
    };

    const handleExpandAll = () => {
        if (chartRef.current) {
            chartRef.current.expandAll();
        }
    };

    const handleCollapseAll = () => {
        if (chartRef.current) {
            chartRef.current.collapseAll();
        }
    };

    const handleZoomOut = () => {
        if (chartRef.current) {
            chartRef.current.zoomOut();
        }
    };

    const handleZoomIn = () => {
        if (chartRef.current) {
            chartRef.current.zoomIn();
        }
    };

    const handleExportPdf = () => {
        if (!chartRef.current) {
            return
        }
        chartRef.current.exportImg({
            save: false,
            onLoad: (base64: string) => {
                var pdf = new jspdf();
                var img = new Image();
                img.src = base64;
                img.onload = function () {
                    pdf.addImage(
                        img,
                        'JPEG',
                        5,
                        5,
                        595 / 3,
                        ((img.height / img.width) * 595) / 3
                    );
                    pdf.save('chart.pdf');
                };
            },
        });
    }


    console.log("CONTROLS", showControls)

    return (
        <div className="relative w-full h-screen bg-gray-900" style={{ minHeight: '100vh' }}>
            {/* Chart Container */}
            <div ref={d3Container} style={{ minHeight: '100vh' }} />
            <LogoContainer showControls={showControls}>
                <img src={logo} alt="Company Logo" style={{ width: '150px', height: 'auto' }} />
            </LogoContainer>
            <ArrowButtonContainer isOpen={!showControls} onClick={() => setShowControls(true)}>
                <span>👆</span>
            </ArrowButtonContainer>
            {/* Action Panel */}
            <ActionButtonContainer isOpen={showControls} >
                <ActionButton onClick={() => setShowControls(false)}>
                    <span>❌</span> close

                </ActionButton>
                {/*
                <ActionButton
                    onClick={handleCollapse}
                >
                    <span>📁</span> collapse
                </ActionButton>
                */}

                <ActionButton
                    onClick={handleFit}
                >
                    <span>🔄 fit</span> 
                </ActionButton>

                <ActionButton
                    onClick={handleSwap}
                >
                    <span>🔀 swap</span> 
                </ActionButton>

                <ActionButton
                    onClick={handleCompact}
                >
                    <span>📊 compact</span>
                </ActionButton>

                {/* 
                <ActionButton
                    onClick={handleCenterActive}
                >
                    <span>🎯</span> center active
                </ActionButton>

                <ActionButton
                    onClick={handleCenter}

                >
                    <span>🔲</span> center
                </ActionButton>

                <ActionButton
                    onClick={handleMark}
                >
                    <span>🖍️</span> mark
                </ActionButton>

                <ActionButton
                    onClick={handleMarkRoot}
                >
                    <span>🛤️</span> mark root
                </ActionButton>

                <ActionButton
                    onClick={handleClearMark}
                >
                    <span>🧹</span> clear mark
                </ActionButton>
                */}
                <ActionButton
                    onClick={handleFullscreen}
                >
                    <span>⛶ fullscreen</span> 
                </ActionButton>
                <ActionButton
                    onClick={handleExportImg}
                >
                    <span>🖼️ export img</span> 
                </ActionButton>
                {/* 

                <ActionButton
                    onClick={handleExportFullImg}
                >
                    <span>🖼️</span> export full img
                </ActionButton>
                */}

                <ActionButton
                    onClick={handleExportSvg}
                >
                    <span>📄 export svg</span> 
                </ActionButton>

                <ActionButton
                    onClick={handleExportPdf}
                >
                    <span>📑 export pdf</span> 
                </ActionButton>

                <ActionButton
                    onClick={handleExpandAll}
                >
                    <span>⬇️ expand all</span>
                </ActionButton>

                <ActionButton
                    onClick={handleCollapseAll}
                >
                    <span>⬆️ collapse all</span> 
                </ActionButton>

                <ActionButton
                    onClick={handleZoomOut}
                >
                    <span>🔍 zoom out</span> 
                </ActionButton>

                <ActionButton
                    onClick={handleZoomIn}
                >
                    <span>🔍 zoom in</span> 
                </ActionButton>
            </ActionButtonContainer>
        </div>
    );
};


/**
 *                             <div style="
                                width: 50px;
                                height: 50px;
                                border-radius: 50%;
                                background: ${node.imageUrl ? `url('${node.imageUrl}')` : '#fff'};
                                background-size: cover;
                                background-position: center;
                                margin: 0 auto 12px auto;
                                border: 3px solid rgba(255,255,255,0.3);
                                ${!node.imageUrl ? 'display: flex; align-items: center; justify-content: center; font-size: 18px; color: #667eea; font-weight: bold;' : ''}
                            ">
                                ${!node.imageUrl ? (node.name ? node.name.charAt(0).toUpperCase() : '?') : ''}
                            </div>
 * 
 * 
 * 
 * 
 */